import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Briefcase, Eye, FileText, Image, Download, MapPin, Phone, Mail, User, ExternalLink } from "lucide-react";

const formatDate = (d) => new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const appStatusColors = { pending: "bg-yellow-900/30 text-yellow-400 border-yellow-800/30", reviewed: "bg-blue-900/30 text-blue-400 border-blue-800/30", accepted: "bg-green-900/30 text-green-400 border-green-800/30", rejected: "bg-red-900/30 text-red-400 border-red-800/30" };
const appStatusLabels = { pending: "Pending", reviewed: "Ditinjau", accepted: "Diterima", rejected: "Ditolak" };

export default function RecruitmentPage() {
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", requirements: "", location: "Palembang", type: "full-time", salary_range: "", is_active: true });
  const [detailApp, setDetailApp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [jRes, aRes] = await Promise.all([api.get("/jobs/all"), api.get("/applications")]);
      setJobs(jRes.data);
      setApps(aRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditing(null); setForm({ title: "", description: "", requirements: "", location: "Palembang", type: "full-time", salary_range: "", is_active: true }); setDialogOpen(true); };
  const openEdit = (j) => { setEditing(j); setForm({ title: j.title, description: j.description, requirements: j.requirements, location: j.location, type: j.type, salary_range: j.salary_range, is_active: j.is_active }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/jobs/${editing.id}`, form); toast.success("Lowongan diperbarui"); }
      else { await api.post("/jobs", form); toast.success("Lowongan ditambahkan"); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteJob = async (id) => { if (!window.confirm("Hapus lowongan?")) return; await api.delete(`/jobs/${id}`); toast.success("Dihapus"); fetchData(); };

  const updateAppStatus = async (aid, status) => {
    await api.put(`/applications/${aid}/status`, { status });
    toast.success("Status diperbarui"); fetchData();
    if (detailApp && detailApp.id === aid) setDetailApp(prev => ({ ...prev, status }));
  };

  const openAppDetail = (a) => { setDetailApp(a); setDetailOpen(true); };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="recruitment-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Rekrutmen</h1><p className="text-gray-400 text-sm mt-1">Kelola lowongan kerja & data pelamar</p></div>
        <Button onClick={openAdd} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-10" data-testid="add-job-btn"><Plus size={16} className="mr-2" />Tambah Loker</Button>
      </div>

      <Tabs defaultValue="jobs" className="w-full">
        <TabsList className="bg-[#0F0F0F] border border-[#262626] rounded-none h-10 w-full">
          <TabsTrigger value="jobs" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><Briefcase size={14} className="mr-1" /> Lowongan ({jobs.length})</TabsTrigger>
          <TabsTrigger value="apps" className="flex-1 rounded-none text-xs font-bold uppercase data-[state=active]:bg-[#E60000] data-[state=active]:text-white"><Eye size={14} className="mr-1" /> Lamaran ({apps.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs">
          <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="jobs-table">
            <table className="w-full text-sm"><thead><tr className="border-b border-[#262626]">
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Posisi</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Tipe</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Lokasi</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Pelamar</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Status</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-gray-500">Aksi</th>
            </tr></thead><tbody>
              {jobs.length === 0 ? <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-500">Belum ada lowongan</td></tr> : jobs.map(j => {
                const appCount = apps.filter(a => a.job_id === j.id).length;
                return (
                  <tr key={j.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A]" data-testid={`job-row-${j.id}`}>
                    <td className="px-5 py-3"><p className="text-white font-semibold">{j.title}</p><p className="text-gray-500 text-xs">{j.salary_range || "-"}</p></td>
                    <td className="px-5 py-3"><Badge variant="secondary" className="text-[10px] uppercase">{j.type}</Badge></td>
                    <td className="px-5 py-3 text-gray-400">{j.location}</td>
                    <td className="px-5 py-3"><span className="text-white font-semibold">{appCount}</span> <span className="text-gray-500 text-xs">pelamar</span></td>
                    <td className="px-5 py-3"><Badge className={j.is_active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}>{j.is_active ? "Aktif" : "Nonaktif"}</Badge></td>
                    <td className="px-5 py-3 text-right flex justify-end gap-2">
                      <button onClick={() => openEdit(j)} className="p-1.5 text-gray-400 hover:text-white hover:bg-[#262626]" data-testid={`edit-job-${j.id}`}><Pencil size={14} /></button>
                      <button onClick={() => deleteJob(j.id)} className="p-1.5 text-gray-400 hover:text-[#E60000] hover:bg-[#262626]" data-testid={`delete-job-${j.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
        </TabsContent>

        <TabsContent value="apps">
          <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="apps-table">
            <table className="w-full text-sm"><thead><tr className="border-b border-[#262626]">
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Pelamar</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Posisi</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Kontak</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Dokumen</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Status</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Tanggal</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-gray-500">Aksi</th>
            </tr></thead><tbody>
              {apps.length === 0 ? <tr><td colSpan={7} className="px-5 py-8 text-center text-gray-500">Belum ada lamaran</td></tr> : apps.map(a => (
                <tr key={a.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A]" data-testid={`app-row-${a.id}`}>
                  <td className="px-5 py-3">
                    <p className="text-white font-semibold">{a.name}</p>
                    {a.address && <p className="text-gray-500 text-xs">{a.address}</p>}
                  </td>
                  <td className="px-5 py-3 text-gray-400">{a.job_title}</td>
                  <td className="px-5 py-3">
                    <p className="text-gray-400 text-xs flex items-center gap-1"><Mail size={10} />{a.email}</p>
                    <p className="text-gray-400 text-xs flex items-center gap-1"><Phone size={10} />{a.phone}</p>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {a.cv_url && <a href={a.cv_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-0.5" data-testid={`cv-link-${a.id}`}><FileText size={12} />CV</a>}
                      {a.photo_url && <a href={a.photo_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-0.5" data-testid={`photo-link-${a.id}`}><Image size={12} />Foto</a>}
                      {!a.cv_url && !a.photo_url && <span className="text-gray-600 text-xs">-</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Select value={a.status} onValueChange={v => updateAppStatus(a.id, v)}>
                      <SelectTrigger className="bg-transparent border-0 p-0 h-auto w-auto" data-testid={`status-select-${a.id}`}>
                        <Badge className={`${appStatusColors[a.status] || ""} border`}>{appStatusLabels[a.status] || a.status}</Badge>
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                        <SelectItem value="pending" className="text-white">Pending</SelectItem>
                        <SelectItem value="reviewed" className="text-white">Ditinjau</SelectItem>
                        <SelectItem value="accepted" className="text-white">Diterima</SelectItem>
                        <SelectItem value="rejected" className="text-white">Ditolak</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">{formatDate(a.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => openAppDetail(a)} className="p-1.5 text-gray-400 hover:text-white hover:bg-[#262626]" data-testid={`view-app-${a.id}`}>
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Job Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-lg rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">{editing ? "Edit Lowongan" : "Tambah Lowongan"}</DialogTitle><DialogDescription className="text-gray-400 text-sm">Isi detail lowongan kerja</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="job-form">
            <div><Label className="text-gray-300 text-xs font-bold uppercase">Posisi</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="job-title-input" /></div>
            <div><Label className="text-gray-300 text-xs font-bold uppercase">Deskripsi</Label><textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[80px] text-sm" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="job-desc-input" /></div>
            <div><Label className="text-gray-300 text-xs font-bold uppercase">Persyaratan</Label><textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[60px] text-sm" value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})} data-testid="job-req-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300 text-xs font-bold uppercase">Lokasi</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="job-location-input" /></div>
              <div><Label className="text-gray-300 text-xs font-bold uppercase">Tipe</Label><Select value={form.type} onValueChange={v => setForm({...form, type: v})}><SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="job-type-select"><SelectValue /></SelectTrigger><SelectContent className="bg-[#1A1A1A] border-[#262626]"><SelectItem value="full-time" className="text-white">Full-time</SelectItem><SelectItem value="part-time" className="text-white">Part-time</SelectItem><SelectItem value="contract" className="text-white">Kontrak</SelectItem></SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300 text-xs font-bold uppercase">Gaji</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.salary_range} onChange={e => setForm({...form, salary_range: e.target.value})} placeholder="Rp 3-5 jt" data-testid="job-salary-input" /></div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer mb-1">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="accent-[#E60000]" data-testid="job-active-checkbox" />
                  <span className="text-gray-300 text-xs font-bold uppercase">Aktif</span>
                </label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="job-submit-btn">{editing ? "Simpan" : "Tambah"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Detail Pelamar</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Posisi: {detailApp?.job_title}</DialogDescription>
          </DialogHeader>
          {detailApp && (
            <div className="space-y-4" data-testid="app-detail">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold flex items-center gap-1"><User size={10} /> Nama</p>
                  <p className="text-white text-sm mt-0.5">{detailApp.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold flex items-center gap-1"><Mail size={10} /> Email</p>
                  <p className="text-white text-sm mt-0.5">{detailApp.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold flex items-center gap-1"><Phone size={10} /> Telepon</p>
                  <p className="text-white text-sm mt-0.5">{detailApp.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold flex items-center gap-1"><MapPin size={10} /> Alamat</p>
                  <p className="text-white text-sm mt-0.5">{detailApp.address || "-"}</p>
                </div>
              </div>

              {detailApp.message && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold mb-1">Pesan / Pengalaman</p>
                  <p className="text-gray-300 text-sm whitespace-pre-line bg-[#1A1A1A] border border-[#262626] p-3">{detailApp.message}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-gray-500 uppercase font-bold mb-2">Dokumen</p>
                <div className="flex flex-wrap gap-3">
                  {detailApp.cv_url ? (
                    <a href={detailApp.cv_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] hover:border-blue-800 px-3 py-2 transition-colors" data-testid="detail-cv-link">
                      <FileText size={16} className="text-blue-400" />
                      <div>
                        <p className="text-xs text-white font-semibold">CV / Resume</p>
                        <p className="text-[10px] text-gray-500">{detailApp.cv_filename || "Download"}</p>
                      </div>
                      <ExternalLink size={12} className="text-gray-500 ml-2" />
                    </a>
                  ) : <span className="text-gray-600 text-xs">CV tidak diupload</span>}

                  {detailApp.photo_url ? (
                    <a href={detailApp.photo_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] hover:border-blue-800 px-3 py-2 transition-colors" data-testid="detail-photo-link">
                      <Image size={16} className="text-green-400" />
                      <div>
                        <p className="text-xs text-white font-semibold">Pas Foto</p>
                        <p className="text-[10px] text-gray-500">{detailApp.photo_filename || "Download"}</p>
                      </div>
                      <ExternalLink size={12} className="text-gray-500 ml-2" />
                    </a>
                  ) : <span className="text-gray-600 text-xs">Foto tidak diupload</span>}
                </div>

                {detailApp.photo_url && (
                  <div className="mt-3">
                    <img src={detailApp.photo_url} alt="Pas foto" className="w-24 h-28 object-cover border border-[#262626]" />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-[#262626]">
                <div>
                  <p className="text-xs text-gray-500">Dilamar: {formatDate(detailApp.created_at)}</p>
                </div>
                <Select value={detailApp.status} onValueChange={v => updateAppStatus(detailApp.id, v)}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-9 rounded-none w-36" data-testid="detail-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    <SelectItem value="pending" className="text-white">Pending</SelectItem>
                    <SelectItem value="reviewed" className="text-white">Ditinjau</SelectItem>
                    <SelectItem value="accepted" className="text-white">Diterima</SelectItem>
                    <SelectItem value="rejected" className="text-white">Ditolak</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
